import { type FC, useCallback, useState } from 'react';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import usePromoMetricData from './hooks/queries/usePromoMetricData';
import useMetricSort from './hooks/ui/useMetricSort';
import { usePromoMetricColumns } from './hooks/ui/useMetricColumns';
import MetricTable from './MetricTable';
import PlanDetailsFooter from '../components/PlanDetailsFooter';
import ViewSelectModal from '../components/ViewSelectModal';
import KpiViewMetricBreadcrumb from './components/KpiViewMetricBreadcrumb';
import type { KpiViewType, MetricType, PromoMetricRow, TimePeriod } from './types';

interface PromoMetricViewProps {
    planId: string;
    dimensions: string[];
    kpis: string[];
    timePeriod: TimePeriod;
    searchKeyword: string;
    kpiViewType: KpiViewType;
    setKpiViewType: (v: KpiViewType) => void;
    metricType: MetricType;
    setMetricType: (v: MetricType) => void;
}

const PromoMetricView: FC<PromoMetricViewProps> = ({
    planId,
    dimensions,
    kpis,
    timePeriod,
    searchKeyword,
    kpiViewType,
    setKpiViewType,
    metricType,
    setMetricType,
}) => {
    const { currentPlanDetails, filterState } = usePlanDetailsContext();
    const { metricPageConfig } = useTpoGenConfig();
    const [showKpiViewModal, setShowKpiViewModal] = useState(false);
    const [showMetricTypeModal, setShowMetricTypeModal] = useState(false);

    const { sortBy, sortOrder, handleTableChange, getColumnSortOrder } = useMetricSort('');

    const data = usePromoMetricData({
        planId,
        dimensions,
        kpis,
        kpiViewType,
        metricType,
        timePeriod,
        searchKeyword,
        sharedFilters: filterState.filters,
        sortBy,
        sortOrder,
    });

    const handleClickKpiView = useCallback(() => setShowKpiViewModal(true), []);
    const handleClickMetric = useCallback(() => setShowMetricTypeModal(true), []);

    const columns = usePromoMetricColumns({
        dimensions,
        kpis,
        kpiViewType,
        metricType,
        getColumnSortOrder,
        onClickKpiView: handleClickKpiView,
        onClickMetric: handleClickMetric,
    });

    const handleApplyKpiView = useCallback(
        (v: KpiViewType) => {
            setKpiViewType(v);
            setShowKpiViewModal(false);
        },
        [setKpiViewType],
    );

    const handleApplyMetricType = useCallback(
        (v: MetricType) => {
            setMetricType(v);
            setShowMetricTypeModal(false);
        },
        [setMetricType],
    );

    return (
        <>
            <KpiViewMetricBreadcrumb kpiViewType={kpiViewType} metricType={metricType} />
            <MetricTable<PromoMetricRow>
                dataSource={data.dataSource}
                columns={columns}
                isPending={data.isPending}
                isFetching={data.isFetching}
                isFetchingNextPage={data.isFetchingNextPage}
                hasNextPage={data.hasNextPage}
                fetchNextPage={data.fetchNextPage}
                handleTableChange={handleTableChange}
            />
            <PlanDetailsFooter
                loadedCount={data.loadedCount}
                totalCount={data.totalCount}
                createdBy={currentPlanDetails?.created_by}
            />
            <ViewSelectModal<KpiViewType>
                open={showKpiViewModal}
                title="KPI View"
                label="Choose KPI view"
                options={metricPageConfig.kpiViewTypeOptions}
                initialValue={kpiViewType}
                onCancel={() => setShowKpiViewModal(false)}
                onApply={handleApplyKpiView}
            />
            <ViewSelectModal<MetricType>
                open={showMetricTypeModal}
                title="Metric"
                label="Choose Metric"
                options={metricPageConfig.metricTypeOptions}
                initialValue={metricType}
                onCancel={() => setShowMetricTypeModal(false)}
                onApply={handleApplyMetricType}
            />
        </>
    );
};

PromoMetricView.displayName = 'PromoMetricView';
export default PromoMetricView;
